import {
  AppstoreOutlined,
  DashboardOutlined,
  DollarOutlined,
  EditOutlined,
  FolderOpenOutlined,
  LoadingOutlined,
  LogoutOutlined,
  MoonOutlined,
  RocketOutlined,
  SettingOutlined,
  ShopOutlined,
  ShoppingOutlined,
  SunOutlined,
} from '@ant-design/icons';
import { css } from '@linaria/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  App,
  Avatar,
  Button,
  Dropdown,
  Flex,
  MenuProps,
  Spin,
  Typography,
} from 'antd';

import { isNil } from 'lodash';

import { useMemo } from 'react';

import { useAccount } from '@lib/api/hooks/auth/use-account';

import { useLogout } from '@lib/api/hooks/auth/use-logout';
import { myStoreOptions } from '@lib/api/options/stores';
import { accountIsAdmin } from '@lib/types';
import { resolveProfilePhotoUrl } from '@lib/utils/profile-photo';
import { ROUTE_PATHS } from '@modules/route-paths';

import { APP_THEME_MODE } from '../../../../config/theme';
import { useAppTheme } from '../../../../config/theme/provider';

interface Props {
  hideName?: boolean;
}

const styles = {
  menu: css`
    &.ant-dropdown .ant-dropdown-menu .ant-dropdown-menu-item {
      border-radius: 10px;
    }
  `,
  avatar: css`
    flex: 0 0 auto;
  `,
  trigger: css`
    height: 44px;
    padding-inline: 8px;
  `,
  account: css`
    min-width: 220px;
    padding-block: 6px;
  `,
  accountName: css`
    max-width: 170px;
  `,
};

const UserDropdown = ({ hideName = false }: Props) => {
  const { modal } = App.useApp();
  const account = useAccount();
  const { data: store } = useQuery({
    ...myStoreOptions(account?.isStoreOwner),
    enabled:
      !isNil(account) &&
      Boolean(account.isStoreOwner) &&
      !isNil(account.verifiedAt) &&
      !accountIsAdmin(account),
  });
  const { logout, logingOut } = useLogout();
  const { mode, toggleTheme } = useAppTheme();

  const isDarkMode = mode === APP_THEME_MODE.DARK;
  const themeActionLabel = isDarkMode
    ? 'Ativar tema claro'
    : 'Ativar tema escuro';

  const handleLogout = () => {
    modal.confirm({
      title: 'Tem certeza que deseja sair da plataforma?',
      onOk: () => logout(),
    });
  };

  const adminItems: MenuProps['items'] = useMemo(() => {
    if (account && accountIsAdmin(account)) {
      return [
        {
          key: 'admin-dashboard',
          label: <Link to={ROUTE_PATHS.ADMIN_HOME}>Painel Administrativo</Link>,
          icon: <DashboardOutlined />,
        },
      ];
    }

    return [];
  }, [account]);

  const storeItems: MenuProps['items'] = useMemo(() => {
    if (account && !account.isStoreOwner) {
      if (!accountIsAdmin(account)) {
        return [
          {
            key: 'open-store',
            label: <Link to={ROUTE_PATHS.SELLER}>Abrir minha loja</Link>,
            icon: <ShopOutlined />,
          },
        ];
      }

      return [];
    }

    return [
      {
        key: 'store-settings',
        label: <Link to={ROUTE_PATHS.SELLER}>Configurações</Link>,
        icon: <SettingOutlined />,
      },
      {
        key: 'store-inventory',
        label: (
          <Link to={ROUTE_PATHS.STORE_FOLDER} params={{ slug: store?.slug }}>
            Minha pasta
          </Link>
        ),
        icon: <FolderOpenOutlined />,
      },
      {
        key: 'store-local',
        label: <Link to={ROUTE_PATHS.LOCAL_OWNER}>Nexo Local</Link>,
        icon: <ShopOutlined />,
      },
      {
        key: 'store-sales',
        label: <Link to={ROUTE_PATHS.STORE_SALES}>Minhas vendas</Link>,
        icon: <DollarOutlined />,
      },
    ];
  }, [account, store]);

  const avatarUrl = resolveProfilePhotoUrl(account?.profilePhotoUrl ?? null);
  const accountName =
    account?.name?.split(' ').slice(0, 2).join(' ') || 'Usuário';
  const accountInitial = accountName.trim().charAt(0).toUpperCase();
  const items: MenuProps['items'] = [
    {
      key: 'account-summary',
      disabled: true,
      label: (
        <Flex className={styles.account} align="center" gap={12}>
          <Avatar src={avatarUrl}>{accountInitial}</Avatar>
          <Flex vertical>
            <Typography.Text className={styles.accountName} ellipsis strong>
              {accountName}
            </Typography.Text>
            <Typography.Text type="secondary">{account?.email}</Typography.Text>
          </Flex>
        </Flex>
      ),
    },
    { type: 'divider' },
    ...adminItems,
    ...(adminItems.length > 0 ? [{ type: 'divider' as const }] : []),
    ...storeItems,
    ...(storeItems.length ? [{ type: 'divider' as const }] : []),
    {
      key: 'purchases',
      label: <Link to={ROUTE_PATHS.PURCHASES}>Minhas compras</Link>,
      icon: <ShoppingOutlined />,
    },
    {
      key: 'decks',
      label: <Link to={ROUTE_PATHS.MY_DECKS}>Meus decks</Link>,
      icon: <AppstoreOutlined />,
    },
    {
      key: 'optimized-purchase',
      label: <Link to={ROUTE_PATHS.OPTIMIZED_PURCHASE}>Compra otimizada</Link>,
      icon: <RocketOutlined />,
    },
    {
      key: 'wallet',
      label: <Link to={ROUTE_PATHS.STORE_FINANCE}>Minha carteira</Link>,
      icon: <DollarOutlined />,
    },
    {
      key: 'profile',
      label: <Link to={ROUTE_PATHS.PROFILE}>Atualizar perfil</Link>,
      icon: <EditOutlined />,
    },
    { type: 'divider' },
    {
      key: 'theme',
      label: themeActionLabel,
      icon: isDarkMode ? <SunOutlined /> : <MoonOutlined />,
      onClick: toggleTheme,
    },
    {
      key: 'logout',
      label: 'Sair',
      icon: logingOut ? (
        <Spin indicator={<LoadingOutlined spin />} size="small" />
      ) : (
        <LogoutOutlined />
      ),
      onClick: handleLogout,
      danger: true,
    },
  ];

  return (
    <Dropdown
      rootClassName={styles.menu}
      menu={{ items }}
      placement="bottomRight"
      trigger={['click']}
    >
      <Button
        className={styles.trigger}
        aria-label={`Abrir opções de ${accountName}`}
        type="text"
      >
        <Flex align="center" gap={8}>
          <Avatar
            className={styles.avatar}
            size={34}
            src={avatarUrl}
            alt={accountName}
          >
            {accountInitial}
          </Avatar>
          {hideName ? null : (
            <Typography.Text ellipsis>{accountName}</Typography.Text>
          )}
        </Flex>
      </Button>
    </Dropdown>
  );
};

export default UserDropdown;
